import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";

import { cn } from "./lib/cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Sol ikon (mockup: zarf/kilit/arama). 20px, fg-faint; alanın sol dolgusunu genişletir. */
  icon?: ReactNode;
}

/**
 * Ortak form kontrol stili — Input/Textarea/Select aynı görünümü paylaşır (kriter 5).
 *
 * ⚠ UI Tur 2 §5: yarıçap `radius-md` (10px) — `radius-lg` kartlarla aynı yuvarlaklıktı,
 * girdi ve kart aynı ölçekte görünmemeli. Kenarlık `border-strong` DEĞİL `border-default`:
 * onaylı tasarımda girdiler koyu çerçeveli kutular değil, zeminden hafifçe ayrılan alanlar.
 * Odakta kenarlık `accent`'e dönüyor — lacivert bir odak kenarlığı, dolu bir düğmeyle
 * karışırdı.
 */
/**
 * ⚠ Yatay dolgu (`px-3.5`) BİLEREK dışarıda: `cn` bir `tailwind-merge` değil, çakışan iki
 * `px-*`/`pl-*` sınıfı aynı anda üretilirse HANGİSİNİN kazanacağı Tailwind'in derleme
 * sırasına kalır, bileşenin kendi mantığına değil. Sol ikonlu Input `pl-10 pr-3.5` yazar,
 * ikonsuz `px-3.5` — ikisi de `fieldClasses`'ı TEK bir yatay dolgu sınıfıyla tamamlar.
 */
export const fieldClasses = cn(
  "w-full rounded-md border border-border-default bg-surface text-sm text-fg",
  "placeholder:text-fg-faint",
  "transition-[border-color,box-shadow]",
  "focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/15",
  "disabled:cursor-not-allowed disabled:bg-canvas disabled:text-fg-soft",
  "aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/20",
);

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, icon, ...rest },
  ref,
) {
  if (!icon) {
    return <input ref={ref} className={cn("h-11 px-3.5", fieldClasses, className)} {...rest} />;
  }
  return (
    <span className="relative flex items-center">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-3.5 flex text-fg-faint [&_svg]:size-5"
      >
        {icon}
      </span>
      <input ref={ref} className={cn("h-11 pr-3.5 pl-10", fieldClasses, className)} {...rest} />
    </span>
  );
});
